import { runtimeConfig } from '@/shared/config'
import {
  lastSessionKind,
  logoutCustomerSession,
  rememberSessionKind,
  sessionExpired,
} from './client'

// Своей формы входа у календаря НЕТ ни для кого. Вход обеих аудиторий — единая
// страница хаба (22-unified-login): сотрудник уходит с неё в Authentik (cookie
// stit_session), заказчик вводит логин и пароль (cookie stit_customer). По 401
// продукт не знает, кто перед ним, поэтому ведёт именно туда, а не на
// id.stit.tech. Выход разведён по issuer'ам своих контуров (00-architecture § 3).
// Публичная бронь (/booking/$token) живёт без сессии и этой линии не касается.

// Пропускаем только внутренний absolute-path ("/calendar"): не "//evil"
// (protocol-relative), не "\..\", без CRLF — защита от open-redirect. Зеркалит
// sanitizeReturnTo BFF — двойная страховка, бэк санитайзит повторно.
export function sanitizeInternalPath(raw: string | null | undefined): string | undefined {
  if (raw?.[0] !== '/' || raw.startsWith('//') || /[\\\r\n]/.test(raw)) return undefined
  return raw
}

// rd для чужого origin — всегда absolute URL (хаб должен знать, куда вернуть).
// Уже-абсолютный берём как есть, относительный путь резолвим через origin.
function absoluteReturnTo(returnTo: string): string {
  if (/^https?:\/\//.test(returnTo)) return returnTo
  try {
    return new URL(returnTo, window.location.origin).href
  } catch {
    return window.location.origin
  }
}

// notice=expired отличает «сессию не удалось продлить» от «ты сюда впервые» —
// страница входа объясняет, почему человека выкинуло.
export function loginUrl(returnTo: string = window.location.href): string {
  const params = new URLSearchParams({ rd: absoluteReturnTo(returnTo) })
  if (sessionExpired()) params.set('notice', 'expired')
  return `${runtimeConfig.HUB_LOGIN_URL}?${params.toString()}`
}

// Реальная браузерная навигация (не router-redirect): уходим на чужой origin
// hub.stit.tech. По умолчанию возвращаемся на текущий полный URL.
export function redirectToLogin(returnTo: string = window.location.href): void {
  window.location.assign(loginUrl(returnTo))
}

// Своей формы логина нет, поэтому «нет сессии» = уводим на вход сразу, без
// промежуточного экрана с кнопкой. Ровно один автопереход за вкладку: если
// вернулись с хаба и сессии всё равно нет (cookie не встала, доступ отозван),
// второй прыжок закольцевал бы продукт со входом — вместо этого false, и
// вызывающий показывает экран входа. Флаг живёт в sessionStorage: переживает
// навигацию на чужой origin и умирает вместе со вкладкой.
const LOGIN_ATTEMPT_KEY = 'auth:login-attempt'

export function autoRedirectToLogin(returnTo?: string): boolean {
  // Приватные режимы/выключенный storage кидают на доступе — без памяти о
  // попытке автопереход неотличим от цикла, поэтому отдаём решение экрану.
  try {
    if (sessionStorage.getItem(LOGIN_ATTEMPT_KEY)) return false
    sessionStorage.setItem(LOGIN_ATTEMPT_KEY, '1')
  } catch {
    return false
  }
  redirectToLogin(returnTo)
  return true
}

// Уже ходили на вход в этой вкладке — значит вернулись без сессии.
export function hasLoginAttempt(): boolean {
  try {
    return sessionStorage.getItem(LOGIN_ATTEMPT_KEY) !== null
  } catch {
    return true
  }
}

export function clearLoginAttempt(): void {
  try {
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)
  } catch {
    // storage недоступен — нечего чистить
  }
}

// Выход гасит сессию у issuer'а её контура — общего логаута нет.
// Сотрудник: id.stit.tech/auth/logout. Контракт хаба — POST (state-changing; GET
// → 405 и уязвим к prefetch/CSRF-логауту), поэтому не location.assign, а
// form-POST навигация: same-site POST шлёт hub-cookie без CORS, браузер сам
// следует 302 на ?rd=.
// Заказчик: company-backend знает только XHR-ручку (/auth/customer/logout,
// cross-origin с credentials), редиректа с ?rd= у неё нет — гасим сессию
// запросом и уводим на единый вход сами. Сеть отвалилась — уводим всё равно:
// cookie переживёт, но человек не должен залипнуть в приложении.
export function redirectToLogout(returnTo: string = window.location.origin): void {
  if (lastSessionKind() === 'customer') {
    void logoutCustomerSession().finally(() => {
      rememberSessionKind(null)
      window.location.assign(loginUrl(returnTo))
    })
    return
  }
  const form = document.createElement('form')
  form.method = 'post'
  form.action = `${runtimeConfig.ID_BASE_URL}/auth/logout?rd=${encodeURIComponent(absoluteReturnTo(returnTo))}`
  document.body.appendChild(form)
  form.submit()
}
